import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Gift, Heart, Play, Sparkles } from 'lucide-react';

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1758874089961-e52549c294c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxqb3lmdWwlMjB3b21hbiUyMHJlY2VpdmluZyUyMGElMjBzdXJwcmlzZSUyMGdpZnQlMjBzbWlsaW5nJTIwd2FybXxlbnwwfHx8fDE3ODE0NzA3MzV8MA&ixlib=rb-4.1.0&q=85';
const REVEAL_IMAGE =
  'https://images.unsplash.com/photo-1699730186236-6abe2a515208?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxqb3lmdWwlMjB3b21hbiUyMHJlY2VpdmluZyUyMGElMjBzdXJwcmlzZSUyMGdpZnQlMjBzbWlsaW5nJTIwd2FybXxlbnwwfHx8fDE3ODE0NzA3MzV8MA&ixlib=rb-4.1.0&q=85';

// Timing for the looping countdown -> reveal animation (ms)
const START_SECONDS = 3;
const TICK_MS = 1100;
const REVEAL_DELAY_MS = 700;
const HOLD_MS = 4800;

// Confetti burst on reveal — direction (x,y), colour, delay
const CONFETTI = [
  { x: -70, y: -50, c: 'bg-primary', d: 0 },
  { x: 68, y: -46, c: 'bg-accent', d: 0.05 },
  { x: -50, y: 34, c: 'bg-accent', d: 0.1 },
  { x: 56, y: 40, c: 'bg-primary', d: 0.08 },
  { x: 0, y: -66, c: 'bg-primary', d: 0.03 },
  { x: -80, y: 6, c: 'bg-accent', d: 0.12 },
  { x: 82, y: 0, c: 'bg-primary', d: 0.06 },
  { x: 24, y: 58, c: 'bg-accent', d: 0.14 },
];

const Confetti = () => (
  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
    {CONFETTI.map((p, i) => (
      <motion.span
        key={i}
        initial={{ opacity: 0, x: 0, y: 0, scale: 0 }}
        animate={{ opacity: [0, 1, 0], x: p.x, y: p.y, scale: [0, 1, 0.4] }}
        transition={{ duration: 1.2, delay: p.d, ease: 'easeOut' }}
        className={`absolute h-2 w-2 rounded-full ${p.c}`}
      />
    ))}
  </div>
);

const CountingView = ({ seconds }) => (
  <motion.div
    key="counting"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0, scale: 0.96 }}
    className="flex flex-col items-center py-2 text-center"
  >
    <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
      <Lock className="h-3.5 w-3.5" /> Unlocking in
    </div>
    <div className="relative flex h-16 w-16 items-center justify-center">
      <span className="absolute h-16 w-16 rounded-full bg-primary/10 animate-heartbeat" />
      <AnimatePresence mode="popLayout">
        <motion.div
          key={seconds}
          initial={{ opacity: 0, y: 12, scale: 0.6 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -12, scale: 1.4 }}
          transition={{ duration: 0.45 }}
          className="font-display text-6xl font-bold leading-none text-foreground"
        >
          {seconds}
        </motion.div>
      </AnimatePresence>
    </div>
    <span className="mt-1 text-[11px] uppercase tracking-widest text-muted-foreground">the group chat is guessing…</span>
  </motion.div>
);

const RevealView = () => (
  <motion.div
    key="revealed"
    initial={{ opacity: 0, rotateX: -90 }}
    animate={{ opacity: 1, rotateX: 0 }}
    transition={{ duration: 0.6 }}
    className="relative space-y-3"
  >
    <Confetti />
    <motion.div
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 280, damping: 15, delay: 0.15 }}
      className="mx-auto flex w-fit items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-primary"
    >
      <Sparkles className="h-3.5 w-3.5" /> Unlocked
    </motion.div>
    <div className="relative overflow-hidden rounded-xl">
      <img src={REVEAL_IMAGE} alt="A treasured memory revealed" className="h-24 w-full object-cover" />
      <div className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 shadow">
        <Play className="h-3 w-3 fill-primary text-primary" />
      </div>
    </div>
    <p className="text-sm font-medium leading-snug text-foreground">
      "Happy birthday, my love. Every photo here is a memory of us."
    </p>
    <div className="flex items-center gap-1.5 text-xs font-semibold text-primary">
      <Heart className="h-3.5 w-3.5 fill-primary" /> Revealed with love
    </div>
  </motion.div>
);

/** Hero photo with a glass card that ticks down, then flips to reveal — looping. */
export const CountdownReveal = () => {
  const [phase, setPhase] = useState('counting'); // 'counting' | 'revealed'
  const [seconds, setSeconds] = useState(START_SECONDS);

  useEffect(() => {
    if (phase === 'counting') {
      if (seconds === 0) {
        const t = setTimeout(() => setPhase('revealed'), REVEAL_DELAY_MS);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => setSeconds((s) => s - 1), TICK_MS);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      setPhase('counting');
      setSeconds(START_SECONDS);
    }, HOLD_MS);
    return () => clearTimeout(t);
  }, [phase, seconds]);

  const revealed = phase === 'revealed';

  return (
    <div className="relative w-full" data-testid="hero-countdown-reveal">
      {/* Warm celebratory glow that blooms on reveal */}
      <AnimatePresence>
        {revealed && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7 }}
            className="pointer-events-none absolute -inset-6 z-0 rounded-[2.5rem] bg-primary/20 blur-3xl"
          />
        )}
      </AnimatePresence>

      <div className="relative z-10 overflow-hidden rounded-[2rem] shadow-2xl ring-1 ring-black/5">
        <img
          src={HERO_IMAGE}
          alt="A woman lighting up with joy as she receives a surprise gift"
          className="h-[420px] w-full object-cover sm:h-[520px]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="absolute -bottom-6 left-4 right-4 z-10 mx-auto max-w-sm sm:left-6 sm:right-auto sm:mx-0"
      >
        <div className="overflow-hidden rounded-2xl border border-white/50 bg-white/80 p-5 shadow-2xl backdrop-blur-xl">
          <div className="mb-3 flex items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Gift className="h-3.5 w-3.5 text-primary" /> Story for Sarah
            </span>
            <span
              className={
                revealed
                  ? 'rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-bold text-primary'
                  : 'rounded-full bg-foreground/5 px-2.5 py-1 text-[11px] font-semibold text-muted-foreground'
              }
            >
              {revealed ? 'From: Mum' : 'From: Mystery'}
            </span>
          </div>

          <AnimatePresence mode="wait">
            {revealed ? <RevealView /> : <CountingView seconds={seconds} />}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};
